import { useState, useEffect } from "react";

// #FORK_WORKLIST_V1 — surfaces every field daily-log job logged under a custom-NNNN identity
// instead of a real Job List DUC. These are "forks": the same physical job gets invoiced under
// its real DUC but logged in the field under a custom ID, so LF Reconciliation can never pair the
// two and they sit amber forever.
//
// #FORK_ALIAS_V1 — link path (Option A, non-destructive). Linking writes an alias
// { customId: { duc, by, at, lf } } to Supabase key `fork-alias`. The daily-log record is NOT
// altered. A later pass (recon read-side) resolves customId -> real DUC at key time so the amber
// clears in the PM dashboards and Revenue rail. Reversible via Undo.
export default function ForkWorklist({ sg, ss, jobs, fmtD, upn, canEdit }) {
  const [logs, setLogs]           = useState({});
  const [inv, setInv]             = useState([]);
  const [alias, setAlias]         = useState({});
  const [manual, setManual]       = useState({});
  const [showLinked, setShowLinked] = useState(false);
  const [loaded, setLoaded]       = useState(false);
  const [open, setOpen]           = useState(false);
  const [q, setQ]                 = useState('');

  useEffect(() => { (async () => {
    const dl = await sg('daily-logs');    if (dl && typeof dl === 'object') setLogs(dl);
    const ri = await sg('revenue-input'); if (Array.isArray(ri)) setInv(ri);
    const al = await sg('fork-alias');    if (al && typeof al === 'object') setAlias(al);
    setLoaded(true);
  })(); }, []);

  const C = { card:'#FFFFFF', border:'#E2E8F0', blue:'#1D4ED8', green:'#15803D',
              yellow:'#B45309', red:'#DC2626', text:'#111827', muted:'#6B7280' };
  const _nd      = s => String(s||'').trim().toUpperCase().replace(/^CP-/,'').replace(/-CP$/,'');
  const isCustom = s => String(s||'').toLowerCase().startsWith('custom-');
  const fLF      = n => (Number(n)||0).toLocaleString();
  const dShort   = d => { try { return fmtD(d); } catch { return String(d||''); } };

  // ── link / unlink (read-merge-write so concurrent users don't clobber the map) ──
  const link = async (forkId, realDuc, totalLF) => {
    const duc = String(realDuc||'').trim();
    if (!duc) return;
    const base = (await sg('fork-alias')) || {};
    // #FORK_DUPGUARD_V1 — two forks pointing at one DUC silently merges their LF and revenue onto that
    // job. It happened: custom-1780598587000 (E4615412, 318 LF) was typed as E4615411 on 8/13 and sat
    // there absorbed into E4615411 until 8/17, while E4615412 read "Not in Job List" on every Revenue
    // entry. The map allowed it without a word. Warn loudly, but let it through — a genuine re-bore or
    // split job can legitimately share a number.
    const clash = Object.keys(base).find(fid => fid !== forkId && _nd((base[fid]||{}).duc) === _nd(duc));
    if (clash && !window.confirm(`\u26a0 ${duc} is ALREADY linked to another field job:\n\n  ${clash}   (${fLF((base[clash]||{}).lf)} LF)\n\nTwo forks on one DUC means that job absorbs BOTH jobs' LF and revenue, and the other number will read "Not in Job List" everywhere.\n\nDouble-check the digits. Link anyway?`)) return;
    if (!window.confirm(`Link field job\n\n  ${forkId}\n\nto real DUC:  ${duc}\n\nThis tells reconciliation they are the same job. The daily log itself is NOT changed, and you can Undo this.`)) return;
    const next = { ...base, [forkId]: { duc, by: upn || '', at: new Date().toISOString(), lf: Number(totalLF)||0 } };
    setAlias(next);
    await ss('fork-alias', next);
  };
  const unlink = async (forkId) => {
    const base = (await sg('fork-alias')) || {};
    const next = { ...base }; delete next[forkId];
    setAlias(next);
    await ss('fork-alias', next);
  };

  // Real-DUC invoice lines indexed by customer (lowercased) — only real DUCs are valid bridge targets.
  const invByCust = {};
  (inv||[]).forEach(l => {
    if (isCustom(l.duc)) return;
    const c = String(l.customer||'').trim().toLowerCase();
    if (!c) return;
    (invByCust[c] = invByCust[c] || []).push({
      duc:l.duc||'', lf:Number(l.lf)||0, pullDate:l.pullDate||'', sup:l.sup||'', pm:l.pm||''
    });
  });

  // Normalized DUCs that exist on the Job List — to tell pure orphans from custom jobs on the list.
  const onList = new Set((jobs||[]).map(j => _nd(j.duc)).filter(Boolean));

  // Aggregate every custom-ID daily-log job from its PULL entries.
  const forks = [];
  Object.entries(logs).forEach(([key, job]) => {
    const id = job.duc || key;
    if (!isCustom(id) && !isCustom(key)) return;
    const pulls = (job.entries||[]).filter(e => String(e.stage||'').toLowerCase() === 'pull');
    if (!pulls.length) return;
    const totalLF  = pulls.reduce((s,e) => s + (Number(e.boreLF)||0), 0);
    const dates    = pulls.map(e => e.date).filter(Boolean).sort();
    const sups     = [...new Set(pulls.map(e => String(e.sup||'').trim()).filter(Boolean))];
    const customer = job.customer || '';
    const cands    = invByCust[String(customer).trim().toLowerCase()] || [];
    const exact    = cands.filter(c => c.lf === totalLF);   // same-customer invoice whose LF matches exactly
    forks.push({
      id, customer, sups, totalLF, count:pulls.length,
      first:dates[0]||'', last:dates[dates.length-1]||'',
      orphan: !onList.has(_nd(id)),
      cands, exact,
    });
  });
  forks.sort((a,b) => b.totalLF - a.totalLF);   // biggest LF exposure first

  const unlinkedAll = forks.filter(f => !alias[f.id]);
  const linkedAll   = forks.filter(f => alias[f.id]);

  const ql = String(q||'').trim().toLowerCase();
  const shown = unlinkedAll.filter(f => !ql
    || f.id.toLowerCase().includes(ql)
    || f.customer.toLowerCase().includes(ql)
    || f.sups.join(' ').toLowerCase().includes(ql));

  const totalForkLF = unlinkedAll.reduce((s,f) => s + f.totalLF, 0);

  if (loaded && !forks.length) return null;   // nothing forked at all → stay out of the way

  const greenBtn  = { background:C.green, color:'#fff', border:'none', borderRadius:5, padding:'4px 9px', fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' };
  const linkBtn   = { background:'#fff', color:C.blue, border:`1px solid ${C.blue}`, borderRadius:5, padding:'4px 9px', fontSize:11, fontWeight:700, cursor:'pointer' };
  const manualInp = { fontSize:11, padding:'4px 6px', border:`1px solid ${C.border}`, borderRadius:5, width:120, boxSizing:'border-box' };
  const undoBtn   = { background:'transparent', color:C.yellow, border:`1px solid ${C.border}`, borderRadius:5, padding:'2px 9px', fontSize:10.5, fontWeight:700, cursor:'pointer' };

  return (
    <div style={{ background:C.card, border:`1px solid ${unlinkedAll.length?C.yellow:C.border}`, borderRadius:10, padding:'8px 12px', marginBottom:6 }}>   {/* #REV_TOPBAR_V2 (2026-08-28) — tightened to sit closer to the Closed Periods/Year line below it */}
      <div onClick={()=>setOpen(o=>!o)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', gap:10 }}>
        <div style={{ fontWeight:800, fontSize:13, color:C.text }}>
          ⚠ Unlinked Field Jobs <span style={{ color:C.muted, fontWeight:600 }}>— logged under custom IDs</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:11.5, color:unlinkedAll.length?C.yellow:C.green, fontWeight:700 }}>
            {loaded
              ? `${unlinkedAll.length} unlinked · ${fLF(totalForkLF)} LF${linkedAll.length?` · ${linkedAll.length} linked`:''}`
              : 'Loading…'}
          </span>
          <span style={{ fontSize:13, color:C.muted }}>{open ? '▴' : '▾'}</span>
        </div>
      </div>

      {open && (
        <div style={{ marginTop:10 }}>
          <div style={{ fontSize:10.5, color:C.muted, lineHeight:1.4, marginBottom:8 }}>
            These bores were logged in the field against a <b>custom-</b> ID instead of a real DUC, so LF
            Reconciliation can't pair them and they show amber. <b style={{ color:C.green }}>Green</b> = a real-DUC
            invoice with matching LF already exists — click <b>Link → DUC</b> to tie them together. Otherwise
            type the correct DUC. Linking is non-destructive (the daily log isn't changed) and can be undone.
          </div>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Filter by custom ID / customer / supervisor…"
            style={{ width:'100%', fontSize:12, padding:'6px 8px', borderRadius:6, border:`1px solid ${C.border}`, marginBottom:8, boxSizing:'border-box' }} />

          <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:520, overflowY:'auto' }}>
            {shown.map(f => (
              <div key={f.id} style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 10px', background: f.exact.length ? '#F0FDF4' : '#FFFBEB' }}>
                <div style={{ display:'flex', justifyContent:'space-between', gap:8, alignItems:'baseline', flexWrap:'wrap' }}>
                  <div style={{ fontWeight:700, fontSize:12.5, color:C.text }}>
                    {f.customer || '—'} <span style={{ color:C.muted, fontWeight:500, fontSize:11 }}>· {f.id}</span>
                  </div>
                  <div style={{ fontSize:12, fontWeight:800, color:C.blue, whiteSpace:'nowrap' }}>{fLF(f.totalLF)} LF</div>
                </div>
                <div style={{ fontSize:10.5, color:C.muted, marginTop:2 }}>
                  {f.count} pull{f.count===1?'':'s'} · {f.sups.join(', ')||'no sup'} · {f.first ? dShort(f.first) : '?'}{f.last && f.last!==f.first ? `–${dShort(f.last)}` : ''}
                  {f.orphan
                    ? <span style={{ color:C.red, fontWeight:700 }}> · not on Job List</span>
                    : <span style={{ color:C.muted }}> · on Job List (custom)</span>}
                </div>
                {f.exact.length > 0 ? (
                  <div style={{ fontSize:10.5, color:C.green, fontWeight:700, marginTop:4 }}>
                    ↔ likely match: {f.exact.map(c => `${c.duc} (${fLF(c.lf)} LF${c.pullDate?` · ${c.pullDate}`:''})`).join('   ·   ')}
                  </div>
                ) : f.cands.length > 0 ? (
                  <div style={{ fontSize:10.5, color:C.yellow, marginTop:4 }}>
                    same-customer invoices: {f.cands.slice(0,4).map(c => `${c.duc} (${fLF(c.lf)} LF)`).join('   ·   ')}{f.cands.length>4?` +${f.cands.length-4} more`:''} <span style={{ color:C.muted }}>— verify the real DUC</span>
                  </div>
                ) : (
                  <div style={{ fontSize:10.5, color:C.muted, marginTop:4 }}>no same-customer invoice found yet</div>
                )}

                {canEdit && <div style={{ display:'flex', gap:6, alignItems:'center', marginTop:6, flexWrap:'wrap' }}>
                  {f.exact.map(c => (
                    <button key={c.duc} onClick={()=>link(f.id, c.duc, f.totalLF)} style={greenBtn}>Link → {c.duc}</button>
                  ))}
                  <input value={manual[f.id]||''} onChange={e=>setManual(m=>({ ...m, [f.id]:e.target.value }))}
                    placeholder="real DUC…" style={manualInp} />
                  <button onClick={()=>link(f.id, manual[f.id], f.totalLF)} disabled={!String(manual[f.id]||'').trim()}
                    style={{ ...linkBtn, opacity:String(manual[f.id]||'').trim()?1:0.4, cursor:String(manual[f.id]||'').trim()?'pointer':'default' }}>Link</button>
                </div>}
              </div>
            ))}
            {/* #FORK_DUPGUARD_V1 — "No matches" sent Bill hunting on 8/17 when the fork he wanted was
                sitting in the collapsed 'linked' section below. If the search term matches something
                already linked, say so and say where. */}
            {!shown.length && (() => {
              const hit = q.trim() ? linkedAll.find(f => _nd(alias[f.id] && alias[f.id].duc).includes(_nd(q)) || String(f.id).toLowerCase().includes(q.trim().toLowerCase())) : null;
              return (
                <div style={{ color:C.muted, fontSize:11.5, padding:'10px 4px', textAlign:'center' }}>
                  {hit ? (
                    <span>Nothing unlinked matches &ldquo;{q}&rdquo; &mdash; but <b style={{ color:C.text }}>{hit.id}</b> is already linked to <b style={{ color:C.text }}>{alias[hit.id].duc}</b>. Open <b style={{ color:C.green }}>&#10003; linked</b> below and hit Undo to change it.</span>
                  ) : unlinkedAll.length ? `No matches for "${q}".` : 'All forks linked. \u{1F389}'}
                </div>
              );
            })()}
          </div>

          {linkedAll.length > 0 && (
            <div style={{ marginTop:10, borderTop:`1px solid ${C.border}`, paddingTop:8 }}>
              <div onClick={()=>setShowLinked(s=>!s)} style={{ cursor:'pointer', fontSize:11, color:C.green, fontWeight:700 }}>
                ✓ {linkedAll.length} linked {showLinked ? '▴' : '▾'}
              </div>
              {showLinked && (
                <div style={{ display:'flex', flexDirection:'column', gap:5, marginTop:6 }}>
                  {linkedAll.map(f => (
                    <div key={f.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, fontSize:10.5, color:C.muted, background:'#F8FAFC', border:`1px solid ${C.border}`, borderRadius:6, padding:'5px 8px' }}>
                      <span>{f.customer || '—'} · {f.id} → <b style={{ color:C.text }}>{alias[f.id].duc}</b> · {fLF(f.totalLF)} LF{alias[f.id].by?` · ${alias[f.id].by}`:''}</span>
                      {canEdit && <button onClick={()=>unlink(f.id)} style={undoBtn}>Undo</button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
